import { Responsible } from '../modules/ResponsiblePartiesAndContractors/types';

export interface EstadoPasosInforme {
    datosGenerales: boolean;
    sustancias: boolean;
    proveedores: boolean;
    equiposRadiologicos: boolean;
    trabajadores: boolean;
}

const NOMBRE_CAMPO_RESPONSABLE: Record<string, string> = {
    nombre: 'nombre',
    apellido: 'apellido',
    cuit: 'CUIL',
    idCargo: 'cargo',
    idTipoContratacion: 'tipo de contratación',
    idRepresentacion: 'representación',
    cantidadHoras: 'cantidad de horas',
    matricula: 'matrícula profesional',
    entidadOtorgante: 'entidad otorgante',
    titulo: 'título habilitante'
};

const NOMBRE_CARGO: Record<string, string> = {
    R: 'Responsable de los Datos del Formulario',
    H: 'Profesional de Higiene y Seguridad en el Trabajo',
    M: 'Profesional de Medicina Laboral',
    C: 'Contratista'
};

const PASOS: Array<{ clave: keyof EstadoPasosInforme; nombre: string }> = [
    { clave: 'datosGenerales', nombre: 'Datos generales' },
    { clave: 'sustancias', nombre: 'Sustancias' },
    { clave: 'proveedores', nombre: 'Proveedores' },
    { clave: 'equiposRadiologicos', nombre: 'Equipos radiológicos' },
    { clave: 'trabajadores', nombre: 'Trabajadores' }
];

/**
 * Traduce lo que frena el envío a frases que el usuario puede resolver: el paso
 * incompleto, o, en Responsables, de quién y qué dato falta. Antes el diálogo
 * sólo decía "faltan datos, revisá las tabs" aunque todos los pasos se vieran
 * completos.
 *
 * Los errores de responsables y contratistas vienen de
 * `validateResponsiblesAndContractorsCarcinogenicData` con la forma
 * `responsiblesData[i].campo`, `contractorsData[i].campo` y
 * `Missing required responsible with code 'X' (...)` / `Only one responsible with code 'X' (...)`.
 */
export const describirFaltantesParaEnviar = (
    pasos: EstadoPasosInforme,
    responsibleErrors: string[],
    contractorErrors: string[],
    responsables: Responsible[]
): string[] => {
    const faltantes: string[] = PASOS.filter(({ clave }) => !pasos[clave]).map(
        ({ nombre }) => `Completá el paso ${nombre}.`
    );

    const camposPorResponsable = new Map<number, string[]>();
    for (const error of responsibleErrors) {
        const campo = /^responsiblesData\[(\d+)\]\.(\w+)$/.exec(error);
        if (campo) {
            const indice = Number(campo[1]);
            const nombre = NOMBRE_CAMPO_RESPONSABLE[campo[2]];
            if (!nombre) continue;
            const lista = camposPorResponsable.get(indice) ?? [];
            if (!lista.includes(nombre)) lista.push(nombre);
            camposPorResponsable.set(indice, lista);
            continue;
        }

        const faltaCargo = /^Missing required responsible with code '(\w)'/.exec(error);
        if (faltaCargo) {
            faltantes.push(`Falta cargar el ${NOMBRE_CARGO[faltaCargo[1]] ?? 'responsable obligatorio'}.`);
            continue;
        }

        const cargoRepetido = /^Only one responsible with code '(\w)'/.exec(error);
        if (cargoRepetido) {
            faltantes.push(
                `Hay más de un ${NOMBRE_CARGO[cargoRepetido[1]] ?? 'responsable con el mismo cargo'}: dejá uno solo.`
            );
            continue;
        }

        if (error === 'responsiblesData is empty') {
            faltantes.push('Cargá los responsables en el paso Responsables.');
        }
    }

    camposPorResponsable.forEach((campos, indice) => {
        const responsable = responsables[indice];
        const quien =
            [responsable?.nombre, responsable?.apellido].filter(Boolean).join(' ').trim() ||
            `el responsable ${indice + 1}`;
        faltantes.push(`A ${quien} le falta: ${campos.join(', ')}.`);
    });

    if (contractorErrors.length) {
        faltantes.push('Hay un contratista con datos incompletos en el paso Responsables.');
    }

    return faltantes;
};
